import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { Checkbox } from '../../../components/ui/Checkbox';

const LeadTable = ({ leads, onLeadAction, selectedLeads, onLeadSelect, onBulkAction }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'score', direction: 'desc' });
  const [expandedLead, setExpandedLead] = useState(null);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedLeads = [...leads]?.sort((a, b) => {
    if (sortConfig?.key === 'score') {
      return sortConfig?.direction === 'asc' ? a?.score - b?.score : b?.score - a?.score;
    }
    if (sortConfig?.key === 'company') {
      return sortConfig?.direction === 'asc' 
        ? a?.company?.localeCompare(b?.company)
        : b?.company?.localeCompare(a?.company);
    }
    if (sortConfig?.key === 'lastActivity') {
      return sortConfig?.direction === 'asc' 
        ? new Date(a.lastActivity) - new Date(b.lastActivity)
        : new Date(b.lastActivity) - new Date(a.lastActivity);
    }
    return 0;
  });

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success bg-success/10';
    if (score >= 70) return 'text-warning bg-warning/10';
    return 'text-muted-foreground bg-muted';
  };

  const getEngagementIcon = (level) => {
    switch (level) {
      case 'high': return { icon: 'TrendingUp', color: 'text-success' };
      case 'medium': return { icon: 'Minus', color: 'text-warning' };
      case 'low': return { icon: 'TrendingDown', color: 'text-destructive' };
      default: return { icon: 'Minus', color: 'text-muted-foreground' };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isAllSelected = selectedLeads?.length === leads?.length && leads?.length > 0;
  const isIndeterminate = selectedLeads?.length > 0 && selectedLeads?.length < leads?.length;

  const handleSelectAll = (checked) => {
    if (checked) {
      onLeadSelect?.(leads?.map(l => l?.id));
    } else {
      onLeadSelect?.([]);
    }
  };

  const handleLeadSelect = (leadId, checked) => {
    if (checked) {
      onLeadSelect?.([...selectedLeads, leadId]);
    } else {
      onLeadSelect?.(selectedLeads?.filter(id => id !== leadId));
    }
  };

  const handleAnalysisAction = (action, lead) => {
    if (action === 'social') {
      onLeadAction?.('viewSocialAnalysis', lead);
    } else if (action === 'website') {
      onLeadAction?.('viewWebsiteAnalysis', lead);
    } else if (action === 'advertising') {
      onLeadAction?.('viewAdvertisingAnalysis', lead);
    }
  };

  if (!leads || leads?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg">
        <div className="text-center py-16 px-6">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-5">
            <Icon name="Users" size={36} className="text-muted-foreground opacity-60" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No leads found</h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
            Use the search filters above to discover leads, or import your existing contacts to get started.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              iconName="Search"
              iconPosition="left"
              onClick={() => document.querySelector('[data-search-trigger]')?.click?.()}
            >
              Search for Leads
            </Button>
            <Button
              variant="outline"
              iconName="Upload"
              iconPosition="left"
              onClick={() => onLeadAction?.('import', null)}
            >
              Import Contacts
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      {/* Table Header with Bulk Actions */}
      {selectedLeads?.length > 0 && (
        <div className="bg-primary/5 border-b border-border p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {selectedLeads?.length} lead{selectedLeads?.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={() => onBulkAction('export', selectedLeads)}
              >
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Mail"
                onClick={() => onBulkAction('email', selectedLeads)}
              >
                Email Campaign
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="UserPlus"
                onClick={() => onBulkAction('addToCrm', selectedLeads)}
              >
                Add to CRM
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Table Body */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="p-3 text-left">
                <Checkbox
                  checked={selectedLeads?.length === leads?.length}
                  onChange={(e) => {
                    if (e?.target?.checked) {
                      onLeadSelect?.(leads?.map(l => l?.id));
                    } else {
                      onLeadSelect?.([]);
                    }
                  }}
                />
              </th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Lead</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Contact</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Score</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Social</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Website</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Ads</th>
              <th className="p-3 text-left text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads?.map((lead) => (
              <tr key={lead?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="p-3">
                  <Checkbox
                    checked={selectedLeads?.includes(lead?.id)}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        onLeadSelect?.([...selectedLeads, lead?.id]);
                      } else {
                        onLeadSelect?.(selectedLeads?.filter(id => id !== lead?.id));
                      }
                    }}
                  />
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                      <Image
                        src={lead?.avatar}
                        alt={lead?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{lead?.name}</p>
                      <p className="text-sm text-muted-foreground">{lead?.title}</p>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <div className="space-y-1">
                    <p className="text-sm text-foreground">{lead?.email}</p>
                    <p className="text-sm text-muted-foreground">{lead?.phone}</p>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(lead?.score)}`}>
                    {lead?.score}%
                  </span>
                </td>
                <td className="p-3">
                  {lead?.socialMedia ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAnalysisAction('social', lead)}
                      className="flex items-center space-x-2"
                    >
                      <Icon name="Share2" size={14} className="text-blue-500" />
                      <div className="text-left">
                        <p className="text-xs font-medium text-foreground">{lead?.socialMedia?.overallScore}/100</p>
                        <p className="text-xs text-muted-foreground">
                          {lead?.socialMedia?.totalReach >= 1000000 
                            ? `${(lead?.socialMedia?.totalReach / 1000000)?.toFixed(1)}M` 
                            : `${(lead?.socialMedia?.totalReach / 1000)?.toFixed(1)}K`} reach
                        </p>
                      </div>
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">No data</span>
                  )}
                </td>
                <td className="p-3">
                  {lead?.website ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAnalysisAction('website', lead)}
                      className="flex items-center space-x-2"
                    >
                      <Icon name="Globe" size={14} className="text-purple-500" />
                      <div className="text-left">
                        <p className="text-xs font-medium text-foreground">SEO: {lead?.website?.seoScore}/100</p>
                        <p className="text-xs text-muted-foreground">
                          {lead?.gmb?.verified ? (
                            <span className="text-success flex items-center">
                              <Icon name="CheckCircle2" size={10} className="mr-1" />
                              GMB Verified
                            </span>
                          ) : (
                            'No GMB'
                          )}
                        </p>
                      </div>
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">No data</span>
                  )}
                </td>
                <td className="p-3">
                  {lead?.advertising ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAnalysisAction('advertising', lead)}
                      className="flex items-center space-x-2"
                    >
                      <Icon name="TrendingUp" size={14} className="text-green-500" />
                      <div className="text-left">
                        <p className="text-xs font-medium text-foreground">
                          {(() => {
                            const activeAds = [
                              lead?.advertising?.googleAds?.active,
                              lead?.advertising?.facebookAds?.active,
                              lead?.advertising?.linkedinAds?.active,
                              lead?.advertising?.instagramAds?.active
                            ]?.filter(Boolean)?.length;
                            return `${activeAds} platforms`;
                          })()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(() => {
                            const totalBudget = (lead?.advertising?.googleAds?.estimatedBudget || 0) +
                              (lead?.advertising?.facebookAds?.estimatedBudget || 0) +
                              (lead?.advertising?.linkedinAds?.estimatedBudget || 0);
                            return totalBudget >= 1000 ? `$${(totalBudget / 1000)?.toFixed(0)}K/mo` : `$${totalBudget}/mo`;
                          })()}
                        </p>
                      </div>
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">No ads</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Phone"
                      onClick={() => onLeadAction('call', lead)}
                      title="Call"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Mail"
                      onClick={() => onLeadAction('email', lead)}
                      title="Email"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="MessageSquare"
                      onClick={() => onLeadAction('sms', lead)}
                      title="SMS"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="MoreHorizontal"
                      onClick={() => onLeadAction('more', lead)}
                      title="More Actions"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadTable;